import { Maximize, Minimize } from 'lucide-react';
import { FC, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';

import { useFormFactor } from '../../../utils/formFactor';

import { Spinner, Heading, Button, Pressable } from '../../../shared/ui';

import { Line, pixelToUnit, unitToPixel } from './types';

const MAX_CLOUD_ID = 100;

// Компонент для отслеживания ошибок загрузки изображений
const CloudImageItem: FC<{
  imageId: number;
  index: number;
  hovered: boolean;
  selected: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  onError: (imageId: number) => void;
  formFactor: 'mobile-portrait' | 'desktop' | 'tablet';
}> = ({ imageId, index, hovered, selected, onMouseEnter, onMouseLeave, onClick, onError, formFactor }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Intersection Observer для lazy loading
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
          }
        });
      },
      { rootMargin: '100px' },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const smallUrl = `https://storage.yandexcloud.net/zingizi/clouds/small/${imageId}.jpg`;

  return (
    <div
      ref={containerRef}
      className={classNames(
        'relative rounded-control cursor-pointer transition-all border-4',
        hovered && !selected ? 'border-success/60' : 'border-transparent',
        {
          'h-[291px]': formFactor !== 'mobile-portrait',
          'w-full': formFactor === 'mobile-portrait',
        },
        formFactor === 'mobile-portrait' && 'min-w-[150px] max-w-[315px]',
      )}
      style={
        formFactor === 'mobile-portrait'
          ? {
              aspectRatio: '150 / 116',
            }
          : undefined
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner className="text-ink-subtle" />
        </div>
      )}
      {inView && (
        <img
          ref={imgRef}
          src={smallUrl}
          alt={`Облако ${index + 1}`}
          className="w-full h-full object-cover rounded-control transition-opacity duration-300"
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => {
            // Если изображение не загрузилось, скрываем элемент
            onError(imageId);
          }}
          style={{ opacity: loaded ? 1 : 0 }}
        />
      )}
      {selected && (
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ borderRadius: '12px' }}>
          <rect
            x="1"
            y="1"
            width="calc(100% - 2px)"
            height="calc(100% - 2px)"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeDasharray="12 6"
            rx="12"
            ry="12"
          />
        </svg>
      )}
    </div>
  );
};

interface CloudsClientProps {
  onDrawLine?: (line: Line) => void;
  onDrawLines?: (lines: Line[]) => void;
  useDataChannelTransport?: boolean;
  onClearDrawing?: () => void;
  onSelectImage?: (imageIndex: number) => void;
  onClientFinished?: () => void;
  onColorChange?: (color: string) => void;
  onUndo?: (linesCount: number) => void;
  initialColor?: string;
  onHideVideoChange?: (hide: boolean) => void;
  /** Мобильный 1:1 на весь экран: заголовок в верхней полосе сцены, кнопки внизу в потоке, без отступа под видео */
  mobile?: boolean;
}

export const CloudsClient: FC<CloudsClientProps> = ({
  onDrawLine,
  onDrawLines,
  useDataChannelTransport = false,
  onClearDrawing,
  onSelectImage,
  onClientFinished,
  onColorChange,
  onUndo,
  initialColor = '#000000',
  onHideVideoChange,
  mobile = false,
}) => {
  // Создаем список всех ID изображений (1-100) без загрузки
  const cloudImageIds = useMemo(() => Array.from({ length: MAX_CLOUD_ID }, (_, i) => i + 1), []);

  // Отслеживаем изображения с ошибками загрузки, чтобы не показывать их
  const [failedImageIds, setFailedImageIds] = useState<Set<number>>(new Set());

  // Фильтруем список, исключая изображения с ошибками
  const validCloudImageIds = useMemo(
    () => cloudImageIds.filter((id) => !failedImageIds.has(id)),
    [cloudImageIds, failedImageIds],
  );

  const [isLoadingBackgroundImage, setIsLoadingBackgroundImage] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [confirmedIndex, setConfirmedIndex] = useState<number | null>(null);
  const [isFinished, setIsFinished] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null); // Отдельный canvas для рисования
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState<{ x: number; y: number } | null>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [activeTool, setActiveTool] = useState<
    'pencil' | 'brush-thin' | 'brush-medium' | 'brush-thick' | 'brush-extra' | 'eraser'
  >('brush-medium');
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string>(initialColor);
  const [lastStroke, setLastStroke] = useState<Line[]>([]); // Последний штрих для отмены
  const currentStrokeRef = useRef<Line[]>([]); // Текущий рисуемый штрих
  const savedCanvasStateRef = useRef<ImageData | null>(null); // Сохраненное состояние canvas для перерисовки
  const savedCanvasStateForUndoRef = useRef<ImageData | null>(null); // Сохраненное состояние canvas перед штрихом для undo
  const [hideVideoForClient, setHideVideoForClient] = useState(false);
  const formFactor = useFormFactor();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);

  // Отслеживание изменения ширины экрана
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Перерисовка canvas
  const redrawCanvas = useCallback((skipSave = false) => {
    const canvas = canvasRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    if (!canvas || !drawingCanvas) return;

    const ctx = canvas.getContext('2d');
    const drawingCtx = drawingCanvas.getContext('2d');
    if (!ctx || !drawingCtx) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();

    // Сохраняем состояние canvas для рисования перед изменением размеров
    // Проверяем, есть ли уже нарисованные линии (canvas не пустой)
    let savedDrawingState: ImageData | null = null;
    if (!skipSave && drawingCanvas.width > 0 && drawingCanvas.height > 0) {
      try {
        const imageData = drawingCtx.getImageData(0, 0, drawingCanvas.width, drawingCanvas.height);
        // Проверяем, не пустой ли canvas (есть ли непрозрачные пиксели)
        const hasContent = imageData.data.some((value, index) => {
          // Проверяем альфа-канал (каждый 4-й элемент начиная с индекса 3)
          return index % 4 === 3 && value > 0;
        });
        if (hasContent) {
          savedDrawingState = imageData;
          // Обновляем savedCanvasStateRef для использования при следующей перерисовке
          savedCanvasStateRef.current = imageData;
        }
      } catch (e) {
        // Игнорируем ошибки при сохранении состояния
      }
    }

    // Устанавливаем размеры для обоих canvas
    canvas.width = rect.width;
    canvas.height = rect.height;
    drawingCanvas.width = rect.width;
    drawingCanvas.height = rect.height;

    // Очищаем фоновый canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Рисуем фоновое изображение
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    }

    // Восстанавливаем состояние canvas для рисования, если оно было сохранено
    // Используем savedCanvasStateRef если он есть (для сохранения всех нарисованных линий),
    // иначе используем savedDrawingState (сохраненное при изменении размеров)
    // Но если skipSave = true, не восстанавливаем состояние (для очистки)
    const stateToRestore = skipSave ? null : savedCanvasStateRef.current || savedDrawingState;
    if (stateToRestore) {
      // Масштабируем сохраненное состояние под новые размеры
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = stateToRestore.width;
      tempCanvas.height = stateToRestore.height;
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx) {
        tempCtx.putImageData(stateToRestore, 0, 0);
        // Рисуем масштабированное изображение на новый canvas
        drawingCtx.drawImage(tempCanvas, 0, 0, drawingCanvas.width, drawingCanvas.height);
      }
    } else {
      // Очищаем canvas для рисования только если не было сохраненного состояния
      drawingCtx.clearRect(0, 0, drawingCanvas.width, drawingCanvas.height);
    }

    // Перерисовываем текущий штрих, если он есть (линии, которые были нарисованы после сохранения состояния)
    if (currentStrokeRef.current.length > 0 && drawingCanvas.width > 0 && drawingCanvas.height > 0) {
      currentStrokeRef.current.forEach((line) => {
        const start = unitToPixel(line.x1, line.y1, drawingCanvas.width, drawingCanvas.height);
        const end = unitToPixel(line.x2, line.y2, drawingCanvas.width, drawingCanvas.height);

        drawingCtx.beginPath();
        drawingCtx.moveTo(start.x, start.y);
        drawingCtx.lineTo(end.x, end.y);

        // Устанавливаем параметры в зависимости от инструмента
        if (line.tool === 'eraser') {
          drawingCtx.globalCompositeOperation = 'destination-out';
          drawingCtx.strokeStyle = 'rgba(0,0,0,1)';
          drawingCtx.lineWidth = 20;
        } else {
          drawingCtx.globalCompositeOperation = 'source-over';
          drawingCtx.strokeStyle = line.color || '#000000';

          // Разные размеры для разных кистей
          switch (line.tool) {
            case 'pencil':
              drawingCtx.lineWidth = 2;
              break;
            case 'brush-thin':
              drawingCtx.lineWidth = 3;
              break;
            case 'brush-medium':
              drawingCtx.lineWidth = 5;
              break;
            case 'brush-thick':
              drawingCtx.lineWidth = 8;
              break;
            case 'brush-extra':
              drawingCtx.lineWidth = 12;
              break;
            default:
              drawingCtx.lineWidth = 3;
          }
        }

        drawingCtx.lineCap = 'round';
        drawingCtx.lineJoin = 'round';
        drawingCtx.stroke();

        // Восстанавливаем режим по умолчанию
        drawingCtx.globalCompositeOperation = 'source-over';
      });
    }
  }, []);

  // Загрузка фонового изображения
  useEffect(() => {
    if (confirmedIndex === null) return;

    const cloudImageId = cloudImageIds[confirmedIndex];
    if (!cloudImageId) return;

    setIsLoadingBackgroundImage(true);
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setIsLoadingBackgroundImage(false);
    };
    img.onerror = () => {
      setIsLoadingBackgroundImage(false);
    };
    img.src = `https://storage.yandexcloud.net/zingizi/clouds/${cloudImageId}.jpg`;
  }, [confirmedIndex, cloudImageIds]);

  // Перерисовка canvas после скрытия лоадера
  useEffect(() => {
    if (!isLoadingBackgroundImage && confirmedIndex !== null && imageRef.current) {
      // Функция для попытки перерисовки с несколькими попытками
      const attemptRedraw = (attempt = 0, maxAttempts = 5) => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        const drawingCanvas = drawingCanvasRef.current;

        if (!container || !canvas || !drawingCanvas) {
          if (attempt < maxAttempts) {
            setTimeout(() => attemptRedraw(attempt + 1, maxAttempts), 100 * (attempt + 1));
          }
          return;
        }

        const rect = container.getBoundingClientRect();
        // Проверяем, что контейнер имеет размеры
        if (rect.width > 0 && rect.height > 0) {
          redrawCanvas();
        } else {
          // Если размеры еще не установлены, пробуем еще раз с увеличивающейся задержкой
          if (attempt < maxAttempts) {
            setTimeout(() => attemptRedraw(attempt + 1, maxAttempts), 100 * (attempt + 1));
          } else {
            // Последняя попытка - принудительная перерисовка даже без размеров
            redrawCanvas();
          }
        }
      };

      // Используем requestAnimationFrame для отложенной перерисовки после рендеринга DOM
      requestAnimationFrame(() => {
        attemptRedraw();
      });
    }
  }, [isLoadingBackgroundImage, confirmedIndex, redrawCanvas]);

  // Обработка изменения размера окна и контейнера
  useEffect(() => {
    if (confirmedIndex === null) return;

    const handleResize = () => redrawCanvas();
    window.addEventListener('resize', handleResize);

    // Используем ResizeObserver для отслеживания изменений размера контейнера
    const container = containerRef.current;
    let resizeObserver: ResizeObserver | null = null;

    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        // Используем requestAnimationFrame для оптимизации
        requestAnimationFrame(() => {
          if (imageRef.current) {
            redrawCanvas();
          }
        });
      });
      resizeObserver.observe(container);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver && container) {
        resizeObserver.unobserve(container);
        resizeObserver.disconnect();
      }
    };
  }, [confirmedIndex, redrawCanvas]);

  // Сброс состояния завершения при выборе нового облака
  useEffect(() => {
    if (confirmedIndex !== null) {
      setIsFinished(false);
      setLastStroke([]);
      savedCanvasStateRef.current = null;
    }
  }, [confirmedIndex]);

  // Синхронизация цвета при изменении извне
  useEffect(() => {
    setSelectedColor(initialColor);
  }, [initialColor]);

  // Получение координат указателя
  const getPointerPosition = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const drawingCanvas = drawingCanvasRef.current;
    if (!drawingCanvas) return null;

    const rect = drawingCanvas.getBoundingClientRect();
    const scaleX = drawingCanvas.width / rect.width;
    const scaleY = drawingCanvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  // Начало рисования
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const pos = getPointerPosition(e);
      if (!pos) return;

      const drawingCanvas = drawingCanvasRef.current;
      const drawingCtx = drawingCanvas?.getContext('2d');

      // Сохраняем текущее состояние рисования перед началом нового штриха для undo
      if (drawingCanvas && drawingCtx && drawingCanvas.width > 0 && drawingCanvas.height > 0) {
        savedCanvasStateForUndoRef.current = drawingCtx.getImageData(0, 0, drawingCanvas.width, drawingCanvas.height);
      }

      setIsDrawing(true);
      setLastPoint(pos);
      lastPointRef.current = pos;
      e.currentTarget.setPointerCapture(e.pointerId);

      // Начинаем новый штрих
      currentStrokeRef.current = [];
    },
    [getPointerPosition],
  );

  // Процесс рисования
  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const currentLastPoint = lastPointRef.current;
      if (!isDrawing || !currentLastPoint) return;

      const pos = getPointerPosition(e);
      if (!pos) return;

      const drawingCanvas = drawingCanvasRef.current;
      const canvas = canvasRef.current;
      if (!drawingCanvas || !canvas) return;

      const drawingCtx = drawingCanvas.getContext('2d');
      if (!drawingCtx) return;

      // Рисуем линию на canvas для рисования
      drawingCtx.beginPath();
      drawingCtx.moveTo(currentLastPoint.x, currentLastPoint.y);
      drawingCtx.lineTo(pos.x, pos.y);

      // Устанавливаем параметры в зависимости от инструмента
      if (activeTool === 'eraser') {
        drawingCtx.globalCompositeOperation = 'destination-out';
        drawingCtx.strokeStyle = 'rgba(0,0,0,1)';
        drawingCtx.lineWidth = 20;
      } else {
        drawingCtx.globalCompositeOperation = 'source-over';
        drawingCtx.strokeStyle = selectedColor;

        // Разные размеры для разных кистей
        switch (activeTool) {
          case 'pencil':
            drawingCtx.lineWidth = 2;
            break;
          case 'brush-thin':
            drawingCtx.lineWidth = 3;
            break;
          case 'brush-medium':
            drawingCtx.lineWidth = 5;
            break;
          case 'brush-thick':
            drawingCtx.lineWidth = 8;
            break;
          case 'brush-extra':
            drawingCtx.lineWidth = 12;
            break;
          default:
            drawingCtx.lineWidth = 3;
        }
      }

      drawingCtx.lineCap = 'round';
      drawingCtx.lineJoin = 'round';
      drawingCtx.stroke();

      // Восстанавливаем режим по умолчанию
      drawingCtx.globalCompositeOperation = 'source-over';

      // Отправляем линию в unit coordinates (0-1)
      const width = canvas.width;
      const height = canvas.height;
      const start = pixelToUnit(currentLastPoint.x, currentLastPoint.y, width, height);
      const end = pixelToUnit(pos.x, pos.y, width, height);

      const line: Line = {
        x1: start.x,
        y1: start.y,
        x2: end.x,
        y2: end.y,
        tool: activeTool,
        color: activeTool === 'eraser' ? undefined : selectedColor,
      };

      onDrawLine?.(line);

      // Добавляем линию в текущий штрих
      currentStrokeRef.current.push(line);

      lastPointRef.current = pos;
      setLastPoint(pos);
    },
    [isDrawing, getPointerPosition, onDrawLine, activeTool, selectedColor],
  );

  // Завершение рисования
  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (isDrawing) {
        setIsDrawing(false);
        setLastPoint(null);
        lastPointRef.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);

        // Сохраняем текущий штрих как последний для возможности отмены
        if (currentStrokeRef.current.length > 0) {
          setLastStroke([...currentStrokeRef.current]);

          // Сохраняем состояние canvas после завершения штриха, чтобы при перерисовке все линии сохранялись
          const drawingCanvas = drawingCanvasRef.current;
          const drawingCtx = drawingCanvas?.getContext('2d');
          if (drawingCanvas && drawingCtx && drawingCanvas.width > 0 && drawingCanvas.height > 0) {
            savedCanvasStateRef.current = drawingCtx.getImageData(0, 0, drawingCanvas.width, drawingCanvas.height);
          }
        }

        // В data-channel режиме линии уже отправляются инкрементально в handlePointerMove.
        // Повторная отправка всего штриха на pointerUp создает дубли и перегружает канал.
        if (!useDataChannelTransport) {
          onDrawLines?.([...currentStrokeRef.current]);
        }
        currentStrokeRef.current = [];
      }
    },
    [isDrawing, onDrawLines, useDataChannelTransport],
  );

  // Отмена последнего штриха
  const handleUndo = useCallback(() => {
    if (lastStroke.length === 0 || !savedCanvasStateForUndoRef.current) return;

    const drawingCanvas = drawingCanvasRef.current;
    const drawingCtx = drawingCanvas?.getContext('2d');

    // Восстанавливаем сохраненное состояние рисования (до начала последнего штриха)
    if (drawingCanvas && drawingCtx && savedCanvasStateForUndoRef.current) {
      drawingCtx.putImageData(savedCanvasStateForUndoRef.current, 0, 0);
      // Обновляем savedCanvasStateRef для перерисовки
      savedCanvasStateRef.current = savedCanvasStateForUndoRef.current;
    }

    // Отправляем событие отмены с количеством линий для синхронизации с психологом
    const linesCount = lastStroke.length;
    onUndo?.(linesCount);

    // Очищаем последний штрих
    setLastStroke([]);
    savedCanvasStateForUndoRef.current = null;
  }, [lastStroke.length, onUndo]);

  // Очистка холста
  const handleClear = useCallback(() => {
    // Очищаем все состояния перед перерисовкой
    currentStrokeRef.current = [];
    setLastStroke([]);
    savedCanvasStateRef.current = null;
    savedCanvasStateForUndoRef.current = null;
    // Перерисовываем canvas без сохранения состояния (skipSave = true)
    redrawCanvas(true);
    onClearDrawing?.();
  }, [redrawCanvas, onClearDrawing]);

  // Обработка подтверждения выбора облака
  const handleConfirmImage = useCallback(
    (index: number) => {
      setConfirmedIndex(index);
      onSelectImage?.(index);
    },
    [onSelectImage],
  );

  // Обработка выбора цвета
  const handleColorChange = useCallback(
    (color: string) => {
      setSelectedColor(color);
      onColorChange?.(color);
    },
    [onColorChange],
  );

  // Обработка выбора инструмента
  const handleToolChange = useCallback((tool: typeof activeTool) => {
    setActiveTool(tool);
    setIsPaletteOpen(false);
  }, []);

  // Если облако уже подтверждено - показываем его в большом формате с возможностью рисования
  if (confirmedIndex !== null) {
    return (
      <div
        className={classNames('h-full w-full flex flex-col bg-ink-muted text-white relative', {
          'px-3 pb-3 pt-1': mobile,
          'p-6': !mobile && !hideVideoForClient,
          'pt-[8rem]': !mobile && formFactor === 'mobile-portrait' && !hideVideoForClient,
        })}
      >
        {/* Кнопка для управления видео в стиле wheel - скрыта на мобильных устройствах */}
        {formFactor !== 'mobile-portrait' && (
          <Button
            type="button"
            size="sm"
            variant="overlay"
            className="absolute top-2 right-2 z-20"
            aria-label={hideVideoForClient ? 'Показать видео' : 'Скрыть видео'}
            startIcon={hideVideoForClient ? <Minimize size={16} aria-hidden /> : <Maximize size={16} aria-hidden />}
            onClick={() => {
              const newValue = !hideVideoForClient;
              setHideVideoForClient(newValue);
              onHideVideoChange?.(newValue);
            }}
          >
            {hideVideoForClient ? 'Уменьшить' : 'Увеличить'}
          </Button>
        )}

        {formFactor !== 'mobile-portrait' && !hideVideoForClient && (
          <Heading tone="inverse" size="lg" className="mb-5">
            Облака
          </Heading>
        )}

        <div
          className={classNames('flex-1 min-h-0', {
            'flex flex-col': formFactor === 'mobile-portrait',
          })}
        >
          <div
            ref={containerRef}
            className={classNames('relative overflow-hidden', {
              'h-[500px] mx-auto': formFactor !== 'mobile-portrait' && !hideVideoForClient,
              'mb-4 mx-auto min-h-[300px]': formFactor === 'mobile-portrait' && !hideVideoForClient,
              'h-full mx-auto': hideVideoForClient,
              'w-[780px]': !hideVideoForClient && formFactor !== 'mobile-portrait',
              'w-full': !hideVideoForClient && formFactor === 'mobile-portrait',
            })}
            style={{
              ...(hideVideoForClient
                ? {
                    width: 'auto',
                    aspectRatio: windowWidth < 700 ? '4/3' : '16/9',
                  }
                : windowWidth < 700
                  ? {
                      aspectRatio: '4/3',
                    }
                  : {}),
            }}
          >
            {isLoadingBackgroundImage ? (
              <div className="absolute inset-0 flex items-center justify-center bg-white rounded-control">
                <Spinner className="text-ink-subtle" />
              </div>
            ) : (
              <>
                {/* Фоновый canvas с изображением */}
                <canvas
                  ref={canvasRef}
                  className="absolute top-0 left-0 w-full h-full rounded-control pointer-events-none"
                />
                {/* Canvas для рисования (поверх фонового) */}
                <canvas
                  ref={drawingCanvasRef}
                  className={classNames(
                    'absolute top-0 left-0 w-full h-full rounded-control',
                    isFinished
                      ? 'cursor-default'
                      : activeTool === 'eraser'
                        ? isDrawing
                          ? 'cursor-grabbing'
                          : 'cursor-grab'
                        : 'cursor-crosshair',
                  )}
                  onPointerDown={isFinished ? undefined : handlePointerDown}
                  onPointerMove={isFinished ? undefined : handlePointerMove}
                  onPointerUp={isFinished ? undefined : handlePointerUp}
                  style={{ touchAction: 'none' }}
                />
                {/* Кнопка "Готово" в увеличенном режиме */}
                {!isFinished && hideVideoForClient && formFactor !== 'mobile-portrait' && (
                  <Button
                    size="lg"
                    className="absolute bottom-4 right-4 z-30 min-w-36"
                    onClick={() => {
                      setIsFinished(true);
                      onClientFinished?.();
                    }}
                  >
                    Готово
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {!isFinished && (
          <div
            className={classNames({
              'absolute bottom-4 left-1/2 -translate-x-1/2 z-30':
                hideVideoForClient && formFactor !== 'mobile-portrait',
              'absolute bottom-4 left-4 right-4 z-30 flex flex-col gap-3 items-center':
                hideVideoForClient && formFactor === 'mobile-portrait',
              'mt-auto mx-auto relative': !hideVideoForClient,
              'w-[780px] flex justify-between items-center': !hideVideoForClient && formFactor !== 'mobile-portrait',
              'w-full flex flex-col gap-3 items-center': !hideVideoForClient && formFactor === 'mobile-portrait',
            })}
          >
            {/* Белая панель с инструментами */}
            <div
              className={classNames('bg-white rounded-card flex items-center shadow-overlay', {
                'p-[8px] gap-[10px]': formFactor !== 'mobile-portrait',
                'p-[6px] gap-[4px] overflow-x-auto flex-nowrap': formFactor === 'mobile-portrait',
              })}
            >
              {/* Кнопка 1 - Карандаш (тонкий) */}
              <Pressable
                onClick={() => handleToolChange('pencil')}
                aria-pressed={activeTool === 'pencil'}
                className={classNames(
                  'flex items-center justify-center rounded-control transition-colors',
                  {
                    'p-[6px]': formFactor !== 'mobile-portrait',
                    'p-[4px] flex-shrink-0': formFactor === 'mobile-portrait',
                  },
                  activeTool === 'pencil' ? 'bg-accent hover:bg-accent' : 'hover:bg-surface-muted',
                )}
                title="Карандаш"
                aria-label="Карандаш"
              >
                <svg
                  className={classNames({
                    'w-9 h-9': formFactor !== 'mobile-portrait',
                    'w-7 h-7': formFactor === 'mobile-portrait',
                  })}
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.99805 33L4.49965 27.7464C4.66286 27.1751 4.96882 26.6548 5.38871 26.2344L29.6103 2.01238C29.9385 1.6843 30.3836 1.5 30.8477 1.5C31.3117 1.5 31.7568 1.6843 32.085 2.01238L33.9856 3.91291C34.3137 4.24108 34.498 4.68613 34.498 5.15017C34.498 5.61421 34.3137 6.05926 33.9856 6.38743L9.764 30.6077C9.34399 31.0289 8.82358 31.336 8.2519 31.5002L2.99805 33Z"
                    stroke="#111026"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M26.3507 6L30.4275 9.9237C32.1412 11.5737 32.998 12.3987 32.998 13.425C32.998 14.448 32.1412 15.273 30.4275 16.923L28.9195 18.375M13.498 18.375L17.7823 22.5"
                    stroke="#111026"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Pressable>

              {/* Кнопка 2 - Кисть тонкая */}
              <Pressable
                onClick={() => handleToolChange('brush-thin')}
                aria-pressed={activeTool === 'brush-thin'}
                className={classNames(
                  'flex items-center justify-center rounded-control transition-colors',
                  {
                    'p-[6px]': formFactor !== 'mobile-portrait',
                    'p-[4px] flex-shrink-0': formFactor === 'mobile-portrait',
                  },
                  activeTool === 'brush-thin' ? 'bg-accent hover:bg-accent' : 'hover:bg-surface-muted',
                )}
                title="Кисть тонкая"
                aria-label="Кисть тонкая"
              >
                <svg
                  className={classNames({
                    'w-9 h-9': formFactor !== 'mobile-portrait',
                    'w-7 h-7': formFactor === 'mobile-portrait',
                  })}
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_183_5311)">
                    <path
                      d="M18.0254 14C19.3351 14.0001 20.2518 14.86 20.3496 15.7715C20.6314 18.3981 21 22.2968 21 24.6826C21 26.5366 20.6662 28.5359 20.3223 30.0947C20.1515 30.8688 19.9802 31.5235 19.8525 31.9834C19.7887 32.2132 19.7358 32.3946 19.6992 32.5166C19.681 32.5773 19.6666 32.624 19.6572 32.6543C19.6526 32.6692 19.6497 32.6805 19.6475 32.6875C19.6463 32.6911 19.645 32.6938 19.6445 32.6953V32.6963L19.6348 32.7246L19.6279 32.7539L19.626 32.7598C19.6245 32.7653 19.6225 32.7747 19.6191 32.7871C19.6124 32.8123 19.602 32.8514 19.5879 32.9014C19.5597 33.0015 19.5167 33.1471 19.4609 33.3223C19.3486 33.6755 19.1864 34.1374 18.9834 34.5918C18.7757 35.0568 18.5486 35.4624 18.3213 35.7354C18.1707 35.9161 18.0747 35.9738 18.0371 35.9922C17.9988 35.9747 17.8944 35.9166 17.7275 35.7227C17.4893 35.4457 17.2493 35.0371 17.0283 34.5713C16.8121 34.1154 16.6359 33.6526 16.5137 33.2988C16.453 33.1233 16.407 32.9773 16.376 32.877C16.3605 32.8269 16.3492 32.788 16.3418 32.7627C16.3381 32.7501 16.3346 32.741 16.333 32.7354L16.332 32.7295L16.3281 32.7158L16.3242 32.7031L16.3232 32.7012C16.3228 32.6997 16.3222 32.6974 16.3213 32.6943C16.3191 32.6872 16.3152 32.6754 16.3105 32.6602C16.3013 32.6297 16.2873 32.5833 16.2695 32.5225C16.2338 32.4004 16.1823 32.2198 16.1201 31.9902C15.9955 31.5301 15.8289 30.8743 15.6621 30.0996C15.3264 28.5402 15 26.5391 15 24.6826C15 22.2937 15.3995 18.381 15.7041 15.7539C15.8091 14.8489 16.724 14 18.0254 14Z"
                      stroke="black"
                      strokeWidth="2"
                    />
                    <path
                      d="M18.75 4.78711L18.7607 4.82031L18.7773 4.85254C18.7778 4.85344 18.7783 4.85535 18.7793 4.85742C18.7814 4.86173 18.7847 4.86884 18.7891 4.87793C18.7977 4.89612 18.8105 4.92405 18.8271 4.95996C18.8606 5.032 18.909 5.13768 18.9668 5.27051C19.0827 5.53697 19.2367 5.91028 19.3877 6.33691C19.6961 7.2085 19.9681 8.23523 19.9326 9.02539C19.8828 10.1335 19.5276 11.4243 19.04 12.4316C18.7963 12.9351 18.5305 13.3443 18.2734 13.6191C18.0035 13.9078 17.8128 13.9766 17.7178 13.9766C17.6227 13.9765 17.4319 13.9076 17.1621 13.6191C16.9051 13.3443 16.6402 12.9351 16.3965 12.4316C15.9088 11.4242 15.5528 10.1336 15.5029 9.02539C15.4674 8.23523 15.7394 7.2085 16.0479 6.33691C16.1988 5.91026 16.3528 5.53699 16.4688 5.27051C16.5265 5.13768 16.5749 5.03201 16.6084 4.95996C16.6251 4.92408 16.6378 4.89612 16.6465 4.87793C16.6508 4.86884 16.6542 4.86173 16.6562 4.85742C16.6573 4.85532 16.6578 4.85345 16.6582 4.85254H16.6592L16.6748 4.82031L16.6855 4.78711L17.7178 1.61523L18.75 4.78711Z"
                      fill="black"
                      stroke="black"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_183_5311">
                      <rect width="36" height="36" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </Pressable>

              {/* Кнопка 3 - Кисть средняя (новая иконка с округлым наконечником) */}
              <Pressable
                onClick={() => handleToolChange('brush-medium')}
                aria-pressed={activeTool === 'brush-medium'}
                className={classNames(
                  'flex items-center justify-center rounded-control transition-colors',
                  {
                    'p-[6px]': formFactor !== 'mobile-portrait',
                    'p-[4px] flex-shrink-0': formFactor === 'mobile-portrait',
                  },
                  activeTool === 'brush-medium' ? 'bg-accent hover:bg-accent' : 'hover:bg-surface-muted',
                )}
                title="Кисть средняя"
                aria-label="Кисть средняя"
              >
                <svg
                  className={classNames({
                    'w-9 h-9': formFactor !== 'mobile-portrait',
                    'w-7 h-7': formFactor === 'mobile-portrait',
                  })}
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.0254 13.6484C19.3587 13.6485 20.2566 14.4983 20.3506 15.3506C20.6325 17.9067 21 21.6976 21 24.0156C21 25.8137 20.6668 27.7543 20.3232 29.2695C20.1527 30.0216 19.982 30.6577 19.8545 31.1045C19.7908 31.3277 19.7377 31.5036 19.7012 31.6221C19.683 31.6812 19.6695 31.7264 19.6602 31.7559C19.6555 31.7705 19.6516 31.7812 19.6494 31.7881C19.6484 31.7911 19.6479 31.7935 19.6475 31.7949L19.6465 31.7969L19.6367 31.8262L19.6289 31.8555C19.6287 31.8564 19.6286 31.8586 19.6279 31.8613C19.6265 31.8667 19.6244 31.8758 19.6211 31.8877C19.6145 31.9119 19.6038 31.9489 19.5898 31.9971C19.5618 32.0941 19.5194 32.2353 19.4639 32.4053C19.3519 32.748 19.1905 33.1963 18.9883 33.6367C18.7815 34.0872 18.5552 34.4781 18.3301 34.7412C18.1682 34.9304 18.0687 34.9807 18.0361 34.9941C18.0012 34.9803 17.8945 34.9282 17.7188 34.7295C17.4825 34.4623 17.2436 34.0668 17.0234 33.6152C16.8081 33.1734 16.6326 32.724 16.5107 32.3809C16.4503 32.2108 16.4039 32.0698 16.373 31.9727C16.3577 31.9241 16.3462 31.8867 16.3389 31.8623C16.3352 31.8502 16.3327 31.8414 16.3311 31.8359L16.3301 31.8301L16.3262 31.8164L16.3213 31.8037V31.8018C16.3208 31.8003 16.3194 31.7981 16.3184 31.7949C16.3162 31.7881 16.3131 31.7772 16.3086 31.7627C16.2994 31.7332 16.2854 31.688 16.2676 31.6289C16.2319 31.5104 16.1803 31.3346 16.1182 31.1113C15.9937 30.6643 15.8277 30.0271 15.6611 29.2744C15.3258 27.7586 15 25.8163 15 24.0156C15 21.6949 15.3994 17.8906 15.7041 15.334C15.8051 14.4874 16.7007 13.6484 18.0254 13.6484Z"
                    stroke="black"
                    strokeWidth="2"
                  />
                  <path
                    d="M20.374 4.05859L20.3877 4.07715L20.4023 4.09375L20.4033 4.09473C20.404 4.09549 20.4048 4.09695 20.4062 4.09863C20.4097 4.10259 20.4155 4.10958 20.4229 4.11816C20.4375 4.13529 20.4599 4.16105 20.4883 4.19531C20.5456 4.26442 20.6285 4.36714 20.7285 4.49707C20.929 4.75771 21.1964 5.12716 21.4629 5.56055C22.0082 6.44755 22.5001 7.52416 22.5 8.46875C22.4998 9.5072 21.9054 10.677 20.998 11.6055C20.0876 12.537 18.9645 13.1221 18.0264 13.1221C17.0869 13.122 15.9504 12.536 15.0264 11.6035C14.1049 10.6735 13.5 9.50401 13.5 8.46875C13.5 7.52411 13.9919 6.44755 14.5371 5.56055C14.8035 5.12713 15.071 4.75772 15.2715 4.49707C15.3715 4.36712 15.4544 4.26442 15.5117 4.19531C15.5401 4.16105 15.5625 4.13529 15.5771 4.11816C15.5845 4.10958 15.5903 4.10259 15.5938 4.09863C15.5952 4.09696 15.596 4.0955 15.5967 4.09473L15.5977 4.09375L15.6113 4.0791L15.623 4.0625L18.0225 0.841797L20.374 4.05859Z"
                    fill="black"
                    stroke="black"
                  />
                </svg>
              </Pressable>

              {/* Кнопка 4 - Кисть толстая (вторая новая иконка) */}
              <Pressable
                onClick={() => handleToolChange('brush-thick')}
                aria-pressed={activeTool === 'brush-thick'}
                className={classNames(
                  'flex items-center justify-center rounded-control transition-colors',
                  {
                    'p-[6px]': formFactor !== 'mobile-portrait',
                    'p-[4px] flex-shrink-0': formFactor === 'mobile-portrait',
                  },
                  activeTool === 'brush-thick' ? 'bg-accent hover:bg-accent' : 'hover:bg-surface-muted',
                )}
                title="Кисть толстая"
                aria-label="Кисть толстая"
              >
                <svg
                  className={classNames({
                    'w-9 h-9': formFactor !== 'mobile-portrait',
                    'w-7 h-7': formFactor === 'mobile-portrait',
                  })}
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.0254 13.6484C19.3587 13.6485 20.2566 14.4983 20.3506 15.3506C20.6325 17.9067 21 21.6976 21 24.0156C21 25.8137 20.6668 27.7543 20.3232 29.2695C20.1527 30.0216 19.982 30.6577 19.8545 31.1045C19.7908 31.3277 19.7377 31.5036 19.7012 31.6221C19.683 31.6812 19.6695 31.7264 19.6602 31.7559C19.6555 31.7705 19.6516 31.7812 19.6494 31.7881C19.6484 31.7911 19.6479 31.7935 19.6475 31.7949L19.6465 31.7969L19.6367 31.8262L19.6289 31.8555C19.6287 31.8564 19.6286 31.8586 19.6279 31.8613C19.6265 31.8667 19.6244 31.8758 19.6211 31.8877C19.6145 31.9119 19.6038 31.9489 19.5898 31.9971C19.5618 32.0941 19.5194 32.2353 19.4639 32.4053C19.3519 32.748 19.1905 33.1963 18.9883 33.6367C18.7815 34.0872 18.5552 34.4781 18.3301 34.7412C18.1682 34.9304 18.0687 34.9807 18.0361 34.9941C18.0012 34.9803 17.8945 34.9282 17.7188 34.7295C17.4825 34.4623 17.2436 34.0668 17.0234 33.6152C16.8081 33.1734 16.6326 32.724 16.5107 32.3809C16.4503 32.2108 16.4039 32.0698 16.373 31.9727C16.3577 31.9241 16.3462 31.8867 16.3389 31.8623C16.3352 31.8502 16.3327 31.8414 16.3311 31.8359L16.3301 31.8301L16.3262 31.8164L16.3213 31.8037V31.8018C16.3208 31.8003 16.3194 31.7981 16.3184 31.7949C16.3162 31.7881 16.3131 31.7772 16.3086 31.7627C16.2994 31.7332 16.2854 31.688 16.2676 31.6289C16.2319 31.5104 16.1803 31.3346 16.1182 31.1113C15.9937 30.6643 15.8277 30.0271 15.6611 29.2744C15.3258 27.7586 15 25.8163 15 24.0156C15 21.6949 15.3994 17.8906 15.7041 15.334C15.8051 14.4874 16.7007 13.6484 18.0254 13.6484Z"
                    stroke="black"
                    strokeWidth="2"
                  />
                  <path
                    d="M17.6514 1.03711C17.8455 0.848269 18.1545 0.848288 18.3486 1.03711L21.6445 4.24414H21.6455C21.646 4.24468 21.647 4.24657 21.6484 4.24805C21.6514 4.25105 21.6566 4.25598 21.6631 4.2627C21.676 4.27613 21.6962 4.29692 21.7217 4.32422C21.7728 4.37909 21.847 4.46057 21.9365 4.56445C22.1166 4.77351 22.3568 5.07032 22.5947 5.4209C23.0848 6.14304 23.5087 7.00869 23.5 7.77832C23.4839 9.19714 22.7721 10.5336 21.707 11.5254C20.6393 12.5196 19.2652 13.1221 18 13.1221C16.7349 13.122 15.3606 12.5196 14.293 11.5254C13.228 10.5336 12.5161 9.1971 12.5 7.77832C12.4913 7.0087 12.9161 6.14304 13.4062 5.4209C13.6441 5.07043 13.8835 4.77346 14.0635 4.56445C14.1532 4.4603 14.2281 4.37913 14.2793 4.32422C14.3049 4.29677 14.3249 4.27613 14.3379 4.2627C14.3442 4.25614 14.3486 4.25106 14.3516 4.24805C14.353 4.24655 14.354 4.24469 14.3545 4.24414L17.6514 1.03711Z"
                    fill="black"
                    stroke="black"
                  />
                </svg>
              </Pressable>

              {/* Разделитель */}
              <div
                className={classNames('border-l-[1px] border-line-strong flex-shrink-0', {
                  'h-[48px]': formFactor !== 'mobile-portrait',
                  'h-[36px]': formFactor === 'mobile-portrait',
                })}
              />

              {/* Кнопка 5 - Ластик */}
              <Pressable
                onClick={() => handleToolChange('eraser')}
                aria-pressed={activeTool === 'eraser'}
                className={classNames(
                  'flex items-center justify-center rounded-control transition-colors',
                  {
                    'p-[6px]': formFactor !== 'mobile-portrait',
                    'p-[4px] flex-shrink-0': formFactor === 'mobile-portrait',
                  },
                  activeTool === 'eraser' ? 'bg-accent hover:bg-accent' : 'hover:bg-surface-muted',
                )}
                title="Ластик"
                aria-label="Ластик"
              >
                <svg
                  className={classNames({
                    'w-9 h-9': formFactor !== 'mobile-portrait',
                    'w-7 h-7': formFactor === 'mobile-portrait',
                  })}
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M28.4992 30.0012H12.7492L6.43424 23.5512C6.15486 23.2701 5.99805 22.89 5.99805 22.4937C5.99805 22.0974 6.15486 21.7172 6.43424 21.4362L21.4342 6.43619C21.7153 6.15681 22.0955 6 22.4917 6C22.888 6 23.2682 6.15681 23.5492 6.43619L31.0492 13.9362C31.3286 14.2172 31.4854 14.5974 31.4854 14.9937C31.4854 15.39 31.3286 15.7701 31.0492 16.0512L17.2492 30.0012M26.9992 19.9512L17.5492 10.5012"
                    stroke="#111026"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Pressable>

              {/* Кнопка палитры */}
              <Pressable
                aria-expanded={isPaletteOpen}
                onClick={() => setIsPaletteOpen(!isPaletteOpen)}
                className={classNames(
                  'flex items-center justify-center rounded-control transition-colors',
                  {
                    'p-[6px]': formFactor !== 'mobile-portrait',
                    'p-[4px] flex-shrink-0': formFactor === 'mobile-portrait',
                  },
                  isPaletteOpen ? 'bg-accent hover:bg-accent' : 'hover:bg-surface-muted',
                )}
                title="Палитра"
                aria-label="Палитра"
              >
                <svg
                  className={classNames({
                    'w-9 h-9': formFactor !== 'mobile-portrait',
                    'w-7 h-7': formFactor === 'mobile-portrait',
                  })}
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 33C19.9698 33 21.9204 32.612 23.7403 31.8582C25.5601 31.1044 27.2137 29.9995 28.6066 28.6066C31.4196 25.7936 33 21.9782 33 18C33 14.0218 31.4196 10.2064 28.6066 7.3934C25.7936 4.58035 21.9782 3 18 3C9.75 3 3 9 3 16.5C3 18.8869 3.94821 21.1761 5.63604 22.864C7.32387 24.5518 9.61305 25.5 12 25.5H14.7C15.15 25.5 15.45 25.8 15.45 26.25C15.45 26.4 15.3 26.55 15.3 26.7C14.7 27.45 14.4 28.35 14.4 29.25C14.25 31.35 15.9 33 18 33ZM18 6C21.1826 6 24.2348 7.26428 26.4853 9.51472C28.7357 11.7652 30 14.8174 30 18C30 21.1826 28.7357 24.2348 26.4853 26.4853C24.2348 28.7357 21.1826 30 18 30C17.55 30 17.25 29.7 17.25 29.25C17.25 28.95 17.4 28.8 17.4 28.65C18 27.9 18.3 27.15 18.3 26.25C18.3 24.15 16.65 22.5 14.55 22.5H12C10.4087 22.5 8.88258 21.8679 7.75736 20.7426C6.63214 19.6174 6 18.0913 6 16.5C6 10.65 11.4 6 18 6ZM26.25 15C25.05 15 24 16.05 24 17.25C24 18.45 25.05 19.5 26.25 19.5C27.45 19.5 28.5 18.45 28.5 17.25C28.5 16.05 27.45 15 26.25 15ZM21.75 9C20.55 9 19.5 10.05 19.5 11.25C19.5 12.45 20.55 13.5 21.75 13.5C22.95 13.5 24 12.45 24 11.25C24 10.05 22.95 9 21.75 9ZM14.25 9C13.05 9 12 10.05 12 11.25C12 12.45 13.05 13.5 14.25 13.5C15.45 13.5 16.5 12.45 16.5 11.25C16.5 10.05 15.45 9 14.25 9ZM9.75 15C8.55 15 7.5 16.05 7.5 17.25C7.5 18.45 8.55 19.5 9.75 19.5C10.95 19.5 12 18.45 12 17.25C12 16.05 10.95 15 9.75 15Z"
                    fill="#111026"
                  />
                </svg>
              </Pressable>

              {/* Разделитель */}
              <div
                className={classNames('border-l-[1px] border-line-strong flex-shrink-0', {
                  'h-[48px]': formFactor !== 'mobile-portrait',
                  'h-[36px]': formFactor === 'mobile-portrait',
                })}
              />

              {/* Кнопка отмены */}
              <Pressable
                onClick={handleUndo}
                disabled={lastStroke.length === 0}
                className={classNames(
                  'flex items-center justify-center rounded-control hover:bg-surface-muted transition-colors disabled:opacity-50',
                  {
                    'p-[6px]': formFactor !== 'mobile-portrait',
                    'p-[4px] flex-shrink-0': formFactor === 'mobile-portrait',
                  },
                )}
                title="Отмена"
                aria-label="Отмена"
              >
                <svg
                  className={classNames({
                    'w-9 h-9': formFactor !== 'mobile-portrait',
                    'w-7 h-7': formFactor === 'mobile-portrait',
                  })}
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 7.5L4.5 15L12 22.5"
                    stroke="#111026"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.5 15H16.5C24.7845 15 31.5 21.7155 31.5 30V31.5"
                    stroke="#111026"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Pressable>

              {/* Кнопка очистки */}
              <Pressable
                onClick={handleClear}
                className={classNames(
                  'flex items-center justify-center rounded-control hover:bg-danger-surface transition-colors',
                  {
                    'p-[6px]': formFactor !== 'mobile-portrait',
                    'p-[4px] flex-shrink-0': formFactor === 'mobile-portrait',
                  },
                )}
                title="Очистить рисунок"
                aria-label="Очистить рисунок"
              >
                <svg
                  className={classNames({
                    'w-9 h-9': formFactor !== 'mobile-portrait',
                    'w-7 h-7': formFactor === 'mobile-portrait',
                  })}
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 10.5H30M7.5 10.5L9 28.5C9 29.2956 9.31607 30.0587 9.87868 30.6213C10.4413 31.1839 11.2044 31.5 12 31.5H24C24.7956 31.5 25.5587 31.1839 26.1213 30.6213C26.6839 30.0587 27 29.2956 27 28.5L28.5 10.5M13.5 10.5V6C13.5 5.60218 13.658 5.22064 13.9393 4.93934C14.2206 4.65804 14.6022 4.5 15 4.5H21C21.3978 4.5 21.7794 4.65804 22.0607 4.93934C22.342 5.22064 22.5 5.60218 22.5 6V10.5M15 18L21 24M21 18L15 24"
                    stroke="#EC5B5B"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Pressable>
            </div>
            {/* Белый прямоугольник палитры */}
            {isPaletteOpen && (
              <div
                className={classNames('absolute bg-white rounded-card p-4', {
                  'left-1/2 -translate-x-1/2': formFactor === 'mobile-portrait',
                })}
                style={{
                  width: formFactor === 'mobile-portrait' ? 'calc(100vw - 32px)' : '268px',
                  height: mobile ? 'auto' : '76px',
                  boxShadow: '0px 9px 11px 0px rgba(0, 0, 0, 0.03)',
                  bottom: '100%',
                  marginBottom: '9px',
                  ...(formFactor !== 'mobile-portrait' && {
                    left: '335px',
                    transform: 'translateX(-50%)',
                  }),
                }}
              >
                <div className="grid grid-cols-10 gap-1">
                  {[
                    '#FFCF55',
                    '#FF9D00',
                    '#FF5E00',
                    '#FB6262',
                    '#DF0000',
                    '#B3ED56',
                    '#45962D',
                    '#047E89',
                    '#0065FF',
                    '#1B75BD',
                    '#CD278D',
                    '#AE3EBD',
                    '#9747FF',
                    '#36EF90',
                    '#14B161',
                    '#015487',
                    '#B78B1C',
                    '#544E40',
                    '#AFAFAF',
                    '#181818',
                  ].map((color, index) => (
                    <div
                      key={index}
                      onClick={() => handleColorChange(color)}
                      className={classNames(
                        'rounded-chip cursor-pointer hover:opacity-80 transition-opacity relative flex items-center justify-center',
                        mobile ? 'aspect-square w-full' : 'w-[20px] h-[20px]',
                      )}
                      style={{
                        backgroundColor: color,
                        border: selectedColor === color ? '1px solid #111026' : 'none',
                      }}
                    >
                      {selectedColor === color && (
                        <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 4.07692L5.57143 9L13 1" stroke="black" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Красная кнопка "Готово" */}
            {!(hideVideoForClient && formFactor !== 'mobile-portrait') && (
              <Button
                size="lg"
                className={formFactor === 'mobile-portrait' ? 'w-full' : 'min-w-36'}
                onClick={() => {
                  setIsFinished(true);
                  onClientFinished?.();
                }}
              >
                Готово
              </Button>
            )}
          </div>
        )}
        {isFinished && mobile && <p className="pt-3 text-center text-ui text-white/80">Рисунок отправлен психологу</p>}
      </div>
    );
  }

  return (
    <div
      className={classNames('h-full flex flex-col bg-ink-muted text-white relative', {
        'w-[840px] pl-6 pr-6 pt-6': !mobile && formFactor !== 'mobile-portrait',
        'w-full pl-4 pr-4 pt-6': !mobile && formFactor === 'mobile-portrait',
        'w-full px-4 pt-1': mobile,
        'pt-[8rem]': !mobile && formFactor === 'mobile-portrait',
      })}
    >
      {!mobile && (
        <Heading tone="inverse" size="lg" className="mb-5">
          Облака
        </Heading>
      )}
      <p className={classNames('leading-relaxed', mobile ? 'mb-3 text-ui' : 'mb-6 text-ui lg:text-body-xl')}>
        Рассмотрите изображения облаков и выберите то, которое наиболее откликается вам в текущий момент
      </p>

      <div className="flex-1 min-h-0">
        <div className="h-full overflow-y-auto white-scroll">
          <div
            className={classNames('grid gap-3', {
              'grid-cols-2 pb-[100px] pr-2': formFactor !== 'mobile-portrait',
              'grid-cols-2 pb-[85px]': formFactor === 'mobile-portrait' && !mobile,
              'grid-cols-2 pb-3': mobile,
            })}
          >
            {validCloudImageIds.map((imageId, index) => {
              // Используем индекс из исходного массива для правильной работы hoveredIndex и selectedIndex
              const originalIndex = cloudImageIds.indexOf(imageId);
              return (
                <CloudImageItem
                  key={imageId}
                  imageId={imageId}
                  index={originalIndex}
                  hovered={hoveredIndex === originalIndex}
                  selected={selectedIndex === originalIndex}
                  onMouseEnter={() => setHoveredIndex(originalIndex)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => setSelectedIndex(originalIndex)}
                  onError={(id) => {
                    // Если изображение не загрузилось, добавляем его в список с ошибками
                    setFailedImageIds((prev) => new Set(prev).add(id));
                  }}
                  formFactor={formFactor === 'mobile-portrait' ? 'mobile-portrait' : 'desktop'}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div
        className={classNames('flex justify-start', {
          'pointer-events-none': !mobile,
          'absolute bottom-[24px] left-6 right-6': !mobile && formFactor !== 'mobile-portrait',
          'absolute bottom-4 left-4 right-4': !mobile && formFactor === 'mobile-portrait',
          'shrink-0 border-t border-white/10 pb-3 pt-3': mobile,
        })}
      >
        <Button
          size="lg"
          fullWidth
          className={classNames('pointer-events-auto', { 'max-w-[780px]': formFactor !== 'mobile-portrait' })}
          disabled={selectedIndex === null}
          onClick={() => selectedIndex !== null && handleConfirmImage(selectedIndex)}
        >
          Выбрать
        </Button>
      </div>
    </div>
  );
};
